/**
 * Seeing the access model — the two views every RBAC system needs and few have.
 *
 *  - `EffectiveAccessView`: everything one account can do, **and the role each permission arrives
 *    through**. "Can they?" is always followed by "why?", and the answer is a role name.
 *  - `AccessCheckPanel`: one question — can this account do this, here? — with the *reason* for the answer.
 *
 * Both are reads of `api.effective_access` and `api.access_check`. Nothing is computed in the browser: a
 * client-side re-implementation of scope inheritance would be a second copy of the rule in `iam.can`, and
 * the day the two disagree this screen would confidently explain an answer the server does not give.
 *
 * The permission box in the check is deliberately free text, not a dropdown. A dropdown can only ever ask
 * about permissions that exist; the failure this tool exists to catch is a check written against one that
 * does not — which refuses everyone, for ever, and looks exactly like a correct denial.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, CircleHelp, MapPinOff, ShieldAlert, XCircle, type LucideIcon } from 'lucide-react';
import { PERMISSIONS } from '@rtcom/domain';
import { rpc, supabase } from '../../lib/supabase';
import {
  Button, EmptyState, ErrorState, LoadingRows, SectionLabel, StatusBadge, Unknown, cx, type Tone,
} from '../../ui/primitives';
import { Field, Input, Select } from '../../ui/forms';
import { SENSITIVITY, type AccessCheck, type AccessReason, type EffectiveAccess, type GrantView } from './types';
import { date as fmtDate } from '../../ui/format';

export interface LocationOption {
  id: string;
  code: string;
  name: string;
  kind: string;
}

export function useLocationOptions(enabled = true) {
  return useQuery({
    queryKey: ['iam-location-options'],
    enabled,
    staleTime: 300_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('id,code,name,kind')
        .eq('status', 'ACTIVE')
        .order('code')
        .limit(500);
      if (error) throw error;
      return data as LocationOption[];
    },
  });
}

const GRANT_STATE: Record<GrantView['state'], { tone: Tone; word: string }> = {
  CURRENT: { tone: 'accepted', word: 'Current' },
  SCHEDULED: { tone: 'queued', word: 'Not started' },
  ENDED: { tone: 'neutral', word: 'Ended' },
  ROLE_RETIRED: { tone: 'neutral', word: 'Role retired' },
};

/* ── Effective access ────────────────────────────────────────────────────────────────────────────── */

export function EffectiveAccessView({ accountId, locationId }: { accountId: string; locationId?: string | null }) {
  const query = useQuery({
    queryKey: ['iam-effective-access', accountId, locationId ?? null],
    queryFn: () =>
      rpc<EffectiveAccess>('effective_access', { p_user_account_id: accountId, p_location_id: locationId ?? null }),
  });
  const [moduleFilter, setModuleFilter] = useState('');

  const byModule = useMemo(() => {
    const groups = new Map<string, EffectiveAccess['permissions']>();
    for (const permission of query.data?.permissions ?? []) {
      if (moduleFilter && permission.module !== moduleFilter) continue;
      const list = groups.get(permission.module) ?? [];
      list.push(permission);
      groups.set(permission.module, list);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [query.data, moduleFilter]);

  if (query.isLoading) return <LoadingRows rows={6} columns={4} />;
  if (query.isError) return <ErrorState error={query.error} retry={() => void query.refetch()} />;
  const access = query.data;
  if (!access) return <EmptyState title="Nothing to show" />;

  const modules = [...new Set(access.permissions.map((p) => p.module))].sort();
  const allowedHere = access.at_location ? new Set(access.at_location.allowed) : null;

  return (
    <div>
      {/* Said once, at the top, so nobody works down a list of live grants and draws the wrong conclusion. */}
      {!access.effective ? (
        <div role="alert" className="flex items-start gap-2 border-b border-refused/30 bg-refused-ground px-4 py-3 text-small text-refused">
          <XCircle size={14} className="mt-0.5 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">None of this is in effect</p>
            <p>{access.blocked_by}</p>
          </div>
        </div>
      ) : null}

      {access.mfa.finding ? (
        <div className="flex items-start gap-2 border-b border-admin/30 bg-admin-ground px-4 py-3 text-small text-admin">
          <ShieldAlert size={14} className="mt-0.5 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">Two-factor finding</p>
            <p>{access.mfa.finding}</p>
            <p className="mt-0.5 opacity-90">
              A role’s MFA flag is policy, not enforcement. Require it on the account (Overview tab) to enforce it.
            </p>
          </div>
        </div>
      ) : null}

      {access.at_location ? (
        <div className="border-b border-line px-4 py-3">
          <SectionLabel>At {access.at_location.location?.code ?? 'this location'}</SectionLabel>
          <p className="mt-0.5 text-small text-ink-muted">
            {access.at_location.allowed.length} permission{access.at_location.allowed.length === 1 ? '' : 's'} reach this
            location; {access.at_location.withheld.length} are held but out of scope here.
            {access.at_location.inherits_from.length > 0
              ? ` Scope inherits from ${access.at_location.inherits_from.map((a) => a.code).join(' → ')}.`
              : ' This location is at the top of its tree, so nothing is inherited.'}
          </p>
        </div>
      ) : null}

      <div className="border-b border-line px-4 py-2">
        <SectionLabel>Grants ({access.grants.length})</SectionLabel>
      </div>
      {access.grants.length === 0 ? (
        <p className="border-b border-line px-4 py-4 text-small text-ink-muted">
          No role has ever been granted to this account. It can sign in and see nothing.
        </p>
      ) : (
        <ul className="divide-y divide-line border-b border-line">
          {access.grants.map((grant) => (
            <li key={grant.id} className="flex items-start justify-between gap-4 px-4 py-2">
              <div className="min-w-0">
                <span className="text-body">{grant.role_name}</span>{' '}
                <span className="code-text text-ink-muted">{grant.role_code}</span>
                <p className="mt-0.5 text-small text-ink-muted">
                  {grant.scope === 'ORGANIZATION'
                    ? 'Organisation-wide'
                    : grant.locations.map((l) => l.code).join(', ') || `${grant.location_ids.length} location(s)`}
                  {grant.valid_to ? ` · until ${fmtDate(grant.valid_to)}` : ''}
                  {grant.granted_by ? ` · granted by ${grant.granted_by}` : ''}
                </p>
              </div>
              <span className={cx('label-text shrink-0', grant.state !== 'CURRENT' && 'text-ink-faint')}>
                {GRANT_STATE[grant.state].word}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2">
        <SectionLabel>Permissions ({access.permissions.length})</SectionLabel>
        <select
          aria-label="Module"
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          className="h-[28px] rounded-[var(--radius-control)] border border-line-strong bg-surface px-2 text-small"
        >
          <option value="">Every module</option>
          {modules.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      {byModule.length === 0 ? (
        <p className="px-4 py-4 text-small text-ink-muted">No permission resolves for this account.</p>
      ) : (
        byModule.map(([module, permissions]) => (
          <div key={module}>
            <div className="bg-surface-sunken px-4 py-1">
              <span className="label-text">{module}</span>
            </div>
            <ul className="divide-y divide-line">
              {permissions.map((permission) => {
                const meta = SENSITIVITY[permission.sensitivity];
                const withheld = allowedHere !== null && !allowedHere.has(permission.code);
                return (
                  <li key={permission.code} className={cx('flex items-center gap-3 px-4 py-1.5', withheld && 'opacity-55')}>
                    <span className="code-text min-w-0 flex-1 truncate">{permission.code}</span>
                    {withheld ? (
                      <span className="inline-flex shrink-0 items-center gap-1 text-small text-ink-muted">
                        <MapPinOff size={12} aria-hidden /> out of scope here
                      </span>
                    ) : null}
                    {permission.sensitivity !== 'NORMAL' ? (
                      <StatusBadge tone={meta.tone} icon={meta.icon}>{meta.word}</StatusBadge>
                    ) : null}
                    <span className="w-44 shrink-0 truncate text-right text-small text-ink-muted" title={`Conferred by ${permission.via.join(', ')}`}>
                      via {permission.via.join(', ')}
                    </span>
                    <span className="label-text w-24 shrink-0 text-right">
                      {permission.organization_wide ? 'Everywhere' : `${permission.location_ids.length} location${permission.location_ids.length === 1 ? '' : 's'}`}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}

/* ── One question, with its reason ───────────────────────────────────────────────────────────────── */

const REASON: Record<AccessReason, { tone: Tone; icon: LucideIcon; word: string }> = {
  ALLOWED: { tone: 'accepted', icon: CheckCircle2, word: 'Allowed' },
  NO_SUCH_PERMISSION: { tone: 'admin', icon: CircleHelp, word: 'No such permission' },
  ACCOUNT_NOT_ACTIVE: { tone: 'refused', icon: XCircle, word: 'Account not active' },
  NO_ERP_ACCESS: { tone: 'refused', icon: XCircle, word: 'No ERP access' },
  NO_ROLE_WITH_PERMISSION: { tone: 'refused', icon: XCircle, word: 'No role carries it' },
  GRANT_NOT_CURRENT: { tone: 'queued', icon: AlertTriangle, word: 'Grant not current' },
  OUT_OF_SCOPE: { tone: 'queued', icon: MapPinOff, word: 'Out of scope' },
};

export function AccessCheckPanel({ accountId }: { accountId: string }) {
  const [permission, setPermission] = useState('');
  const [locationId, setLocationId] = useState('');
  const [asked, setAsked] = useState<{ permission: string; locationId: string } | null>(null);
  const locations = useLocationOptions();

  const check = useQuery({
    queryKey: ['iam-access-check', accountId, asked?.permission, asked?.locationId],
    enabled: asked !== null,
    queryFn: () =>
      rpc<AccessCheck>('access_check', {
        p_user_account_id: accountId,
        p_permission: asked!.permission,
        p_location_id: asked!.locationId || null,
      }),
  });

  const result = check.data;
  const meta = result ? REASON[result.reason] : null;

  return (
    <div>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (permission.trim()) setAsked({ permission: permission.trim(), locationId });
        }}
        className="grid grid-cols-[minmax(0,1fr)_minmax(0,14rem)_auto] items-end gap-3 px-4 py-3"
      >
        <Field label="Permission code" hint="Typed, not picked: a code that does not exist is the most useful thing this can find.">
          <Input value={permission} onChange={(e) => setPermission(e.target.value)} placeholder="farm.collection.view" list="iam-permission-codes" className="font-mono" />
        </Field>
        <Field label="At location (optional)">
          <Select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            placeholder="Anywhere"
            options={(locations.data ?? []).map((l) => ({ value: l.id, label: `${l.code} · ${l.name}` }))}
          />
        </Field>
        <Button type="submit" variant="primary" disabled={!permission.trim() || check.isFetching}>
          {check.isFetching ? 'Checking…' : 'Check'}
        </Button>
        <datalist id="iam-permission-codes">
          {PERMISSIONS.map((p) => (
            <option key={p.code} value={p.code} />
          ))}
        </datalist>
      </form>

      {check.isError ? <ErrorState error={check.error} /> : null}

      {result && meta ? (
        <div className="border-t border-line px-4 py-3">
          <div className="flex items-center gap-2">
            <StatusBadge tone={meta.tone} icon={meta.icon}>{meta.word}</StatusBadge>
            <span className="code-text">{result.permission}</span>
          </div>
          <p className="mt-2 max-w-prose text-body">{result.detail}</p>

          {result.nearest && result.nearest.length > 0 ? (
            <p className="mt-2 text-small text-ink-muted">
              Closest real codes:{' '}
              {result.nearest.map((code, i) => (
                <span key={code}>
                  {i > 0 ? ', ' : ''}
                  <button type="button" className="code-text text-brand hover:underline" onClick={() => { setPermission(code); setAsked({ permission: code, locationId }); }}>
                    {code}
                  </button>
                </span>
              ))}
            </p>
          ) : null}

          {result.roles_that_would && result.roles_that_would.length > 0 ? (
            <p className="mt-2 text-small text-ink-muted">
              Roles that carry it: <span className="code-text">{result.roles_that_would.join(', ')}</span>
            </p>
          ) : null}

          {result.covers && result.covers.length > 0 ? (
            <p className="mt-2 text-small text-ink-muted">
              Held at: <span className="code-text">{result.covers.map((c) => c.code).join(', ')}</span>
            </p>
          ) : null}

          {result.grants && result.grants.length > 0 ? (
            <ul className="mt-2 space-y-0.5 border-l-2 border-line pl-3">
              {result.grants.map((g) => (
                <li key={g.grant_id} className="text-small text-ink-muted">
                  <span className="code-text text-ink">{g.role}</span> · {g.scope === 'ORGANIZATION' ? 'organisation-wide' : 'scoped'} ·{' '}
                  {g.current ? 'current' : g.valid_to ? `ended ${fmtDate(g.valid_to)}` : 'not current'}
                </li>
              ))}
            </ul>
          ) : result.reason !== 'NO_SUCH_PERMISSION' ? (
            <p className="mt-2 text-small text-ink-muted">Conferred by: <Unknown title="No grant carries this permission" /></p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
